import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import { useVoice } from '../context/VoiceContext';
import ProgressBar from '../components/ProgressBar';
import AIDisclaimer from '../components/AIDisclaimer';
import InfoTip from '../components/InfoTip';

/** 取「昨天」的日期串，用于漏服提示 */
function shiftDate(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 从「儿子 王小明 138xxxx」这类自由文本里抽出可拨打的号码 */
function extractPhone(text) {
  const m = String(text || '').match(/1\d{10}|0\d{2,3}-?\d{7,8}|\d{7,8}/);
  return m ? m[0] : '';
}

export default function Home() {
  const { settings } = useSettings();
  const { speak } = useVoice();
  const [todayReminders, setTodayReminders] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [badges, setBadges] = useState([]);
  const [missed, setMissed] = useState({ yesterday: 0, today: 0 });
  const [loading, setLoading] = useState(true);

  const loadData = () => {
    const yesterday = shiftDate(-1);
    Promise.all([
      api.getTodayReminders(),
      api.getStreak(),
      api.getBadges(),
      // 漏服数是这个产品最该让用户看见的信息，所以在首页直接算出来
      api.getRecordStats(`?startDate=${yesterday}&endDate=${yesterday}`).catch(() => ({ missed: 0 })),
      api.getRecordStats(`?startDate=${shiftDate(0)}&endDate=${shiftDate(0)}`).catch(() => ({ missed: 0 })),
    ])
      .then(([reminders, streakData, badgesData, yStats, tStats]) => {
        setTodayReminders(reminders);
        setStreak(streakData);
        setBadges(badgesData);
        setMissed({ yesterday: (yStats && yStats.missed) || 0, today: (tStats && tStats.missed) || 0 });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const takenCount = todayReminders.filter(r => r.status === 'taken' || r.status === 'confirmed').length;
  const totalCount = todayReminders.length;

  const handleGreeting = () => {
    const hour = new Date().getHours();
    let greeting = '您好';
    if (hour < 11) greeting = '早上好';
    else if (hour < 14) greeting = '中午好';
    else if (hour < 18) greeting = '下午好';
    else greeting = '晚上好';

    // 未服用的提醒（排除已服用/已确认）
    const pending = todayReminders.filter(r => r.status !== 'taken' && r.status !== 'confirmed');
    const pendingCount = pending.length;

    // 播报文案走「关怀」的口径（见设计文档 §7.3）：
    // 这是这个产品唯一会主动开口的地方，语气就是它对老人说话的样子。
    // 但「关怀」不等于含糊 —— 次数、药名一个不少，只是说法从"汇报"改成"叮嘱"。
    let msg = `${greeting}！今天一共 ${totalCount} 次药，已经吃了 ${takenCount} 次，还有 ${pendingCount} 次没吃哦。`;
    if (pendingCount > 0) {
      msg += ' 接下来要吃的是：';
      pending.forEach((r, i) => {
        msg += `${r.reminder_time}，${r.medicine_name}，${r.dosage} ${r.unit || '片'}。`;
      });
      msg += ' 记得按时吃哦。';
    } else {
      msg += ' 今天的药都吃完啦，好好休息哦。';
    }
    speak(msg);
  };

  // 手动确认已服用
  const handleManualConfirm = async (reminder) => {
    try {
      await api.confirmReminder(reminder.id, { action: 'taken' });
      speak(`${reminder.medicine_name}，已记下，请放心。`);
      loadData();
    } catch (err) {
      alert('确认失败：' + err.message);
    }
  };

  if (loading) return <div className="loading">加载中...</div>;

  const earnedBadges = badges.filter(b => b.earned);

  return (
    <div className="app-container">
      <h1 className="page-title">{settings.app_name}</h1>

      {/*
        漏服提示卡。这是本产品的设计亮点：**用哄的语气，说一件不轻松的事**。
        漏掉一次药是老人最容易出现、也最容易自我责备的事，所以说法的取舍是：
          · 不用「漏服」——那是记录页的状态术语，摆在安慰性提示里像在打报告；
            状态标签照旧用「漏服」（准确），提示语改用口语（「忘吃」）。
          · 不指责。「忘吃」把事说成一次人之常情的疏忽，而不是「你没按时吃」。
          · 不催昨天的补服。昨天的剂量补不回来，硬催只会让人焦虑甚至补双份，
            所以昨天只给「今天记得」，把动作指向未来。
          · 今天还留了窗口，才劝「早点补上」——比无条件的「补上就好」安全，
            不暗示可以拖、更不暗示可以加倍。

        整卡只有一行：图标 + 文案 + 右侧 ›。
        截图反馈原来这里是三行 —— 26px 图标独占一列、文案两行、再加一行
        「点这里查看详情 ›」。那行「点这里」纯粹是在复述「可以点」，
        换成箭头同样能读出来，而且不占行。

        昨天和今天分开说，是刻意的：昨天的已经过去无法补救，今天还可能补上，
        两句话的语气必须不一样（见下方文案）。

        文案长度是被量出来的，不是估出来的（scripts/line-count-probe.html 的 ①-4 候选表）。
        三条硬结论，改文案前请先读：
          1）默认字号不是样式表 :root 里的 16px 兜底值 —— 那只是 JS 起来前的占位。
             默认设置 font_size: 'large' 会把 --font-size-small 提到 18px，
             提示条比按 16px 估出来的宽约 12%。**必须按 18px 反推**。
          2）「昨天」那句的两个词都舍不得删（「药」保清晰、「哦」保语气），
             14 字在 360px 下差约 14px 放不下。解法的两条腿缺一不可：
             ① 数字两侧不留空格（省约 9px）——「1次药」而不是「1 次药」；
             ② .missed-banner 收几何（见 global.css：内边距 12、间距 6、数字无 padding）。
             这样 360px 起就能一行，不只是 375px —— 360 是安卓主力宽度。
          3）320px 仍然放不下，那是宽度物理上限，再删字只会把话说残。
             探针在 320 会明确报「没有任何候选能一行（含最短候选）」。
      */}
      {(missed.today > 0 || missed.yesterday > 0) && (
        <Link to="/records" style={{ textDecoration: 'none' }}>
          <div className="missed-banner">
            <span className="missed-icon">⚠️</span>
            <span className="missed-text">
              {missed.today > 0 && (
                <span>
                  今天<b>{missed.today}</b>次药没吃，早点补上哦
                </span>
              )}
              {missed.today > 0 && missed.yesterday > 0 && <br />}
              {missed.yesterday > 0 && (
                <span>
                  昨天忘吃<b>{missed.yesterday}</b>次药，今天记得吃哦
                </span>
              )}
            </span>
            <span className="missed-more">›</span>
          </div>
        </Link>
      )}

      {/*
        通知家人。
        紧跟漏服提示、放在今日进度之前，是因为这两个模块回答的是同一类问题——
        "我这次没吃/不确定该不该吃，该找谁说"。老人自己解决不了的时候，
        第一屏就要能看见出口，不能让他往下翻。
      */}
      {(() => {
        const contacts = [settings.emergency_contact_1, settings.emergency_contact_2]
          .map(v => String(v || '').trim())
          .filter(Boolean);
        if (contacts.length === 0) return null;
        return (
          <div className="card">
            <h2 style={{ fontSize: 'var(--font-size-large)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              📞 通知家人
              {/* 原来这段说明单独占两行，其实只是解释按钮什么时候用，折进气泡即可 */}
              <InfoTip title="什么时候用？">
                身体不舒服、或者拿不准这药该不该吃时，打给家人说一声。
              </InfoTip>
            </h2>
            {contacts.map((c, i) => {
              const phone = extractPhone(c);
              return phone ? (
                <a key={i} href={`tel:${phone}`} style={{ textDecoration: 'none' }}>
                  <button
                    className="btn-secondary"
                    style={{ width: '100%', minHeight: 56, marginBottom: 8, whiteSpace: 'normal' }}
                  >
                    拨打 {c}
                  </button>
                </a>
              ) : (
                <div
                  key={i}
                  style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-small)', marginBottom: 8 }}
                >
                  {c}
                  {/* 提示语**单独一行**，不跟称呼拼在一起。
                      称呼是用户自己填的自由文本（「老伴儿 张大娘」这类很常见），长度不受控；
                      拼在一行时经常被挤成两行，而且断点随机、可能正好把称呼截在中间。
                      拆开之后这一行的一行是**可保证**的（探针 ⑬-3 实测），
                      称呼再长也只会自己占一行，不会连累提示语。 */}
                  <div style={{ marginTop: 4 }}>没识别到号码，去设置里补全吧</div>
                </div>
              );
            })}
            {/* 这个按钮只用来联系家人，绝不能让人误以为它能救命，所以必须写死这句。
                但不必占两行：核心信息（只联系家人 / 不是急救 / 拨 120）一字不少。 */}
            <div className="ai-disclaimer" style={{ marginTop: 4 }}>
              只联系家人，不是急救通道；紧急拨 120
            </div>
          </div>
        );
      })()}

      {/* 今日进度 + 连续打卡合并成一张卡。
          这两块说的都是「我最近做得怎么样」，拆成两张卡要多付一张卡的内边距（40px）
          加卡片间距（14px），而首屏高度正是这个页面最缺的东西。 */}
      <div className="card">
        <ProgressBar value={takenCount} max={totalCount} label="今日用药进度" />
        {totalCount === 0 && (
          <p style={{ textAlign: 'center', color: 'var(--text-light)', marginTop: 12 }}>
            今天不用吃药，休息吧
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          {totalCount > 0 && (
            <button
              className="btn-secondary"
              style={{
                flex: '1 1 150px',
                minWidth: 0,
                minHeight: 48,
                padding: '0 12px',
                fontSize: 'var(--font-size-small)',
                whiteSpace: 'normal',
              }}
              onClick={handleGreeting}
            >
              🔊 播报今日提醒
            </button>
          )}
          {/* 连续 / 最长：手机两行、电脑一行的**自适应**（用户要求）。
              不写媒体查询，而是交给 flex-wrap —— "放不放得下"本来就只取决于可用宽度，
              让浏览器自己折比写死一个断点更贴近真实（断点写 600 还是 560，
              在断点附近的宽度上都会给出难看的答案）。
              两个计数各自 nowrap，所以折行只会整块换行，不会把「连续 3 天」拆成两半。
              行数由 scripts/line-count-probe.html 的 ⑫ 在手机档与电脑档分别实测。 */}
          <div className="streak-block">
            <span className="streak-item">
              🔥 连续 <b style={{ color: 'var(--primary-color)' }}>{streak.current_streak}</b> 天
            </span>
            <span className="streak-item">最长 {streak.longest_streak} 天</span>
          </div>
        </div>
      </div>

      {/* 今日提醒列表 */}
      <div className="card">
        <h2 style={{ fontSize: 'var(--font-size-large)', marginBottom: 12 }}>今日提醒</h2>
        {todayReminders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📭</div>
            <p>今天不用吃药哦</p>
          </div>
        ) : (
          todayReminders.map(r => (
            <div key={r.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
              {/* flexWrap：超大号字号下药名较长时，状态标签落到下一行，而不是把药名挤没 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ fontWeight: 700 }}>{r.reminder_time}</span>
                  <span style={{ marginLeft: 12 }}>{r.medicine_name}</span>
                  <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>
                    {r.dosage} {r.unit || '片'}
                  </span>
                </div>
                <span className={`tag ${r.status === 'taken' || r.status === 'confirmed' ? 'tag-success' : r.status === 'missed' ? 'tag-danger' : r.status === 'skipped' ? 'tag-warning' : 'tag-info'}`}>
                  {r.status === 'taken' || r.status === 'confirmed' ? '已服用' : r.status === 'missed' ? '已错过' : r.status === 'skipped' ? '已跳过' : '待服用'}
                </span>
              </div>
              {r.status !== 'taken' && r.status !== 'confirmed' && (
                <button
                  className="btn-primary"
                  style={{ width: '100%', minHeight: 44, marginTop: 8, fontSize: 'var(--font-size-small)', whiteSpace: 'normal' }}
                  onClick={() => handleManualConfirm(r)}
                >
                  ✅ 手动确认已服用
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* 徽章：首页只需要回答「我攒到东西了」，完整徽章墙在设置页。
          原来把整面徽章格子铺在首页，每多一枚就往下长一行，首屏直接被推走。 */}
      {earnedBadges.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <h2 style={{ fontSize: 'var(--font-size-large)', margin: 0 }}>我的徽章</h2>
            {/* 带 hash 跳转。设置页收到 #badges 会**自动展开**徽章墙并滚到那一块 ——
                原来只跳到 /settings 顶部，用户还得自己往下翻半页去找，等于没跳。
                做法见 Settings.js 的 location.hash 处理。 */}
            <Link
              to="/settings#badges"
              style={{
                fontSize: 'var(--font-size-small)',
                color: 'var(--primary-color)',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              共 {earnedBadges.length} 枚，全部 ›
            </Link>
          </div>
          <div className="badge-strip">
            {earnedBadges.slice(0, 4).map(b => (
              <span key={b.id} className="badge-chip">
                <span className="badge-chip-icon">{b.icon}</span>
                <span className="badge-chip-name">{b.name}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 快捷操作 */}
      <Link to="/medicines/add" style={{ textDecoration: 'none' }}>
        <button className="btn-primary btn-large" style={{ marginTop: 8 }}>
          ➕ 添加药品
        </button>
      </Link>

      <AIDisclaimer />
    </div>
  );
}